import Swiper from 'swiper';
import { A11y, FreeMode, Mousewheel, Navigation, Thumbs, Zoom } from 'swiper/modules';
import '@styles/deal-gallery.scss';

const VIEWER_SELECTOR = '[data-deal-image-viewer]';
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 1;

function initDealGallery( gallery: HTMLElement ): Swiper | null {
	const mainElement = gallery.querySelector<HTMLElement>( '[data-gallery-main]' );
	const thumbsElement = gallery.querySelector<HTMLElement>( '[data-gallery-thumbs]' );
	const previousButton = gallery.querySelector<HTMLButtonElement>( '.ofertelio-deal-gallery__arrow--previous' );
	const nextButton = gallery.querySelector<HTMLButtonElement>( '.ofertelio-deal-gallery__arrow--next' );
	const thumbButtons = Array.from( gallery.querySelectorAll<HTMLButtonElement>( '[data-gallery-thumb]' ) );
	const imageButtons = Array.from( mainElement?.querySelectorAll<HTMLButtonElement>( '[data-gallery-open]' ) ?? [] );

	if ( ! mainElement || ! thumbsElement || ! previousButton || ! nextButton || thumbButtons.length < 2 ) {
		return null;
	}

	const thumbsSwiper = new Swiper( thumbsElement, {
		modules: [ A11y, FreeMode, Mousewheel, Navigation ],
		a11y: {
			enabled: true,
			firstSlideMessage: 'Esta es la primera miniatura',
			lastSlideMessage: 'Esta es la última miniatura',
			nextSlideMessage: 'Mostrar miniaturas siguientes',
			prevSlideMessage: 'Mostrar miniaturas anteriores',
		},
		freeMode: {
			enabled: true,
			momentumBounce: false,
		},
		mousewheel: {
			forceToAxis: true,
			releaseOnEdges: true,
		},
		navigation: {
			nextEl: nextButton,
			prevEl: previousButton,
		},
		slidesPerView: 'auto',
		spaceBetween: 8,
		watchOverflow: true,
		watchSlidesProgress: true,
	} );

	const mainSwiper = new Swiper( mainElement, {
		modules: [ A11y, Thumbs ],
		a11y: {
			enabled: true,
			containerMessage: 'Visor principal de la galería',
			itemRoleDescriptionMessage: 'Imagen',
			slideLabelMessage: '{{index}} de {{slidesLength}}',
		},
		grabCursor: true,
		spaceBetween: 16,
		speed: 280,
		thumbs: {
			autoScrollOffset: 1,
			swiper: thumbsSwiper,
		},
	} );

	const keepActiveThumbnailVisible = ( index: number ): void => {
		window.requestAnimationFrame( () => {
			const activeSlide = thumbsSwiper.slides[ index ];
			if ( ! activeSlide ) {
				return;
			}

			const viewport = thumbsElement.getBoundingClientRect();
			const thumbnail = activeSlide.getBoundingClientRect();
			if ( thumbnail.left < viewport.left || thumbnail.right > viewport.right ) {
				thumbsSwiper.slideTo( index, 220 );
			}
		} );
	};

	const syncThumbnailState = (): void => {
		thumbButtons.forEach( ( button, index ) => {
			const isActive = index === mainSwiper.activeIndex;
			button.setAttribute( 'aria-pressed', String( isActive ) );
			button.toggleAttribute( 'aria-current', isActive );
		} );

		keepActiveThumbnailVisible( mainSwiper.activeIndex );
	};

	thumbButtons.forEach( ( button, index ) => {
		button.addEventListener( 'click', () => mainSwiper.slideTo( index ) );
	} );

	mainElement.addEventListener( 'keydown', ( event ) => {
		let targetIndex: number | null = null;

		switch ( event.key ) {
			case 'ArrowLeft':
				targetIndex = mainSwiper.activeIndex - 1;
				break;
			case 'ArrowRight':
				targetIndex = mainSwiper.activeIndex + 1;
				break;
			case 'Home':
				targetIndex = 0;
				break;
			case 'End':
				targetIndex = mainSwiper.slides.length - 1;
				break;
			default:
				return;
		}

		event.preventDefault();
		const clampedIndex = Math.max( 0, Math.min( targetIndex, mainSwiper.slides.length - 1 ) );
		const shouldMoveFocus = event.target instanceof HTMLButtonElement && event.target.hasAttribute( 'data-gallery-open' );
		const focusActiveImage = (): void => imageButtons[ clampedIndex ]?.focus( { preventScroll: true } );
		const willChangeSlide = clampedIndex !== mainSwiper.activeIndex;

		if ( shouldMoveFocus && willChangeSlide ) {
			mainSwiper.once( 'transitionEnd', focusActiveImage );
		}

		mainSwiper.slideTo( clampedIndex );

		if ( shouldMoveFocus && ! willChangeSlide ) {
			focusActiveImage();
		}
	} );

	mainSwiper.on( 'slideChange', syncThumbnailState );
	mainSwiper.on( 'transitionEnd', syncThumbnailState );
	gallery.classList.add( 'is-ready' );
	syncThumbnailState();

	return mainSwiper;
}

function initLightbox( viewer: HTMLElement, mainSwiper: Swiper | null ): void {
	const dialog = viewer.querySelector<HTMLDialogElement>( '[data-gallery-dialog]' );
	const lightboxElement = viewer.querySelector<HTMLElement>( '[data-gallery-lightbox]' );
	const openButtons = Array.from( viewer.querySelectorAll<HTMLButtonElement>( '[data-gallery-open]' ) );
	const closeButton = viewer.querySelector<HTMLButtonElement>( '[data-gallery-close]' );
	const previousButton = viewer.querySelector<HTMLButtonElement>( '[data-gallery-lightbox-previous]' );
	const nextButton = viewer.querySelector<HTMLButtonElement>( '[data-gallery-lightbox-next]' );
	const zoomOutButton = viewer.querySelector<HTMLButtonElement>( '[data-gallery-zoom-out]' );
	const zoomInButton = viewer.querySelector<HTMLButtonElement>( '[data-gallery-zoom-in]' );
	const zoomStatus = viewer.querySelector<HTMLOutputElement>( '[data-gallery-zoom-status]' );

	if ( ! dialog || ! lightboxElement || ! closeButton || ! zoomOutButton || ! zoomInButton || ! zoomStatus || openButtons.length === 0 ) {
		return;
	}

	let lightboxSwiper: Swiper | null = null;
	let lastOpener: HTMLButtonElement | null = null;

	const syncZoomControls = ( scale = lightboxSwiper?.zoom.scale ?? MIN_ZOOM ): void => {
		const normalizedScale = Math.max( MIN_ZOOM, Math.min( scale, MAX_ZOOM ) );
		zoomStatus.value = `${ Math.round( normalizedScale * 100 ) }%`;
		zoomStatus.textContent = zoomStatus.value;
		zoomOutButton.disabled = normalizedScale <= MIN_ZOOM;
		zoomInButton.disabled = normalizedScale >= MAX_ZOOM;
	};

	const createLightboxSwiper = (): Swiper => {
		const swiper = new Swiper( lightboxElement, {
			modules: [ A11y, Navigation, Zoom ],
			a11y: {
				enabled: true,
				containerMessage: 'Vista ampliada de la galería',
				itemRoleDescriptionMessage: 'Imagen ampliada',
				nextSlideMessage: 'Mostrar imagen siguiente',
				prevSlideMessage: 'Mostrar imagen anterior',
				slideLabelMessage: '{{index}} de {{slidesLength}}',
			},
			navigation: previousButton && nextButton ? {
				nextEl: nextButton,
				prevEl: previousButton,
			} : false,
			observer: true,
			observeParents: true,
			speed: 240,
			watchOverflow: true,
			zoom: {
				maxRatio: MAX_ZOOM,
				minRatio: MIN_ZOOM,
				panOnMouseMove: false,
				toggle: true,
			},
		} );

		swiper.on( 'zoomChange', ( _, scale ) => syncZoomControls( scale ) );
		swiper.on( 'slideChange', () => {
			swiper.zoom.out();
			syncZoomControls();
			mainSwiper?.slideTo( swiper.activeIndex );
		} );

		return swiper;
	};

	const openLightbox = ( index: number, opener: HTMLButtonElement ): void => {
		if ( typeof dialog.showModal !== 'function' ) {
			return;
		}

		lastOpener = opener;
		dialog.showModal();
		document.body.classList.add( 'ofertelio-deal-lightbox-open' );
		lightboxSwiper ??= createLightboxSwiper();
		lightboxSwiper.update();
		lightboxSwiper.slideTo( index, 0, false );
		lightboxSwiper.zoom.out();
		syncZoomControls();
		closeButton.focus();
	};

	openButtons.forEach( ( button, buttonIndex ) => {
		button.addEventListener( 'click', () => {
			if ( mainSwiper && ! mainSwiper.allowClick ) {
				return;
			}

			const index = Number.parseInt( button.dataset.galleryIndex ?? String( buttonIndex ), 10 );
			openLightbox( Number.isNaN( index ) ? buttonIndex : index, button );
		} );
	} );

	closeButton.addEventListener( 'click', () => dialog.close() );
	dialog.addEventListener( 'click', ( event ) => {
		if ( event.target === dialog ) {
			dialog.close();
		}
	} );
	dialog.addEventListener( 'close', () => {
		document.body.classList.remove( 'ofertelio-deal-lightbox-open' );
		lightboxSwiper?.zoom.out();
		syncZoomControls();
		lastOpener?.focus();
	} );

	zoomOutButton.addEventListener( 'click', () => {
		if ( ! lightboxSwiper ) {
			return;
		}

		const targetScale = Math.max( MIN_ZOOM, lightboxSwiper.zoom.scale - ZOOM_STEP );
		targetScale === MIN_ZOOM ? lightboxSwiper.zoom.out() : lightboxSwiper.zoom.in( targetScale );
	} );
	zoomInButton.addEventListener( 'click', () => {
		if ( ! lightboxSwiper ) {
			return;
		}

		lightboxSwiper.zoom.in( Math.min( MAX_ZOOM, lightboxSwiper.zoom.scale + ZOOM_STEP ) );
	} );

	dialog.addEventListener( 'keydown', ( event ) => {
		if ( ! lightboxSwiper ) {
			return;
		}

		switch ( event.key ) {
			case 'ArrowLeft':
				event.preventDefault();
				lightboxSwiper.slidePrev();
				break;
			case 'ArrowRight':
				event.preventDefault();
				lightboxSwiper.slideNext();
				break;
			case '+':
			case '=':
				event.preventDefault();
				zoomInButton.click();
				break;
			case '-':
			case '_':
				event.preventDefault();
				zoomOutButton.click();
				break;
		}
	} );
}

function initDealImageViewer( viewer: HTMLElement ): void {
	const gallery = viewer.querySelector<HTMLElement>( '[data-deal-gallery]' );
	const mainSwiper = gallery ? initDealGallery( gallery ) : null;
	initLightbox( viewer, mainSwiper );
}

function initDealImageViewers(): void {
	document.querySelectorAll<HTMLElement>( VIEWER_SELECTOR ).forEach( initDealImageViewer );
}

document.addEventListener( 'DOMContentLoaded', initDealImageViewers );

export {};
